import { BillingProduct, BillingSubscription } from '@pollination-solutions/pollination-sdk'
import React, { useEffect, useMemo, useState } from 'react'
import { createContext } from 'react'
import { getProductGroups } from '../utils'

type PaymentMethodProviderProps = {
  children: React.ReactNode
  billingProducts: BillingProduct[]
  existingSubscriptions: BillingSubscription[]
}

type PaymentMethodType = {
  billingPeriod: 'monthly' | 'yearly'
  setBillingPeriod: React.Dispatch<React.SetStateAction<'monthly' | 'yearly'>>
  paymentMethod: 'card' | 'invoice'
  setPaymentMethod: React.Dispatch<React.SetStateAction<'card' | 'invoice'>>
  softwareProducts?: ReturnType<typeof getProductGroups>['softwareProducts']
  seatProducts?: ReturnType<typeof getProductGroups>['seatProducts']
}

export const PaymentMethod = createContext({} as PaymentMethodType)
export const usePaymentMethod = () => {
  const context = React.useContext(PaymentMethod)
  if (!context)
    throw new Error('usePaymentMethod must be used within a PaymentMethodProvider')

  return context
}

export const PaymentMethodProvider = ({ 
  children, 
  billingProducts,
  existingSubscriptions
}: PaymentMethodProviderProps) => {
  const [billingPeriod, setBillingPeriod] = useState<'monthly' | 'yearly'>('yearly')
  const [paymentMethod, setPaymentMethod] = useState<'card' | 'invoice'>('card')

  // Reset payment method to card when switching to monthly billing
  useEffect(() => {
    if (billingPeriod === 'monthly' && paymentMethod === 'invoice') {
      setPaymentMethod('card')
    }
  }, [billingPeriod, paymentMethod])

  const { softwareProducts, seatProducts } = useMemo(() => {
    if (!billingPeriod || !billingProducts || billingProducts.length === 0) {
      return {
        softwareProducts: [],
        seatProducts: []
      }
    }
    return getProductGroups(billingProducts, existingSubscriptions, billingPeriod)
  }, [billingProducts, billingPeriod, existingSubscriptions])

  return (
    <PaymentMethod.Provider
      value={{
        billingPeriod: billingPeriod,
        setBillingPeriod: setBillingPeriod,
        paymentMethod: paymentMethod,
        setPaymentMethod: setPaymentMethod,
        seatProducts,
        softwareProducts
      }}
    >
      {children}
    </PaymentMethod.Provider>
  )
}
